import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

type QueryUpdater = (prevQuery: ParsedUrlQuery) => ParsedUrlQuery;

export function useQueryParams() {
	const router = useRouter();
	const { pathname, query } = router;

	const updateQueryParam = useCallback(
		(updaterOrNameOrObject: QueryUpdater | string | ParsedUrlQuery, value?: string | null) => {
			let newQuery: ParsedUrlQuery;

			if (typeof updaterOrNameOrObject === 'function') {
				// Handle updater function that receives the previous query
				newQuery = updaterOrNameOrObject(query);
			} else if (typeof updaterOrNameOrObject === 'string') {
				// Handle name/value pair
				const name = updaterOrNameOrObject;
				newQuery = { ...query };

				if (value === null || value === '') {
					delete newQuery[name];
				} else {
					newQuery[name] = value;
				}
			} else {
				// Handle object by merging it with the current query
				newQuery = {
					...updaterOrNameOrObject,
				};
			}

			router.push(
				{
					pathname,
					query: newQuery,
				},
				undefined,
				{ scroll: false },
			);
		},
		[pathname, query, router],
	);

	const deleteQueryParam = useCallback(
		(name: string) => {
			(updateQueryParam as (name: string, value: string | null) => void)(name, null);
		},
		[updateQueryParam],
	);

	return {
		updateQueryParam: updateQueryParam as {
			(updater: QueryUpdater): void;
			(queryObject: ParsedUrlQuery): void;
			(name: string, value: string | null): void;
		},
		deleteQueryParam,
		searchParams: query,
	};
}
